import { TEST_EMAIL_DOMAIN } from '.'

// The fixed identity of the synthetic verification organisation. It is provisioned and
// torn down around every scheduled verification run by the internal-ops synthetic-org
// endpoints; apps/verification-tests/e2e mirrors the email as a literal (standalone
// package, cannot import from here), so any change must be applied in both places.
export const SYNTHETIC_ORG_ID = 'f7268ab2-c754-451b-b59a-16214173980a'
export const SYNTHETIC_USER_ID = '53276356-0930-4074-847b-2531732657f7'
export const SYNTHETIC_ORG_NAME = 'Synthetic Verification'
export const SYNTHETIC_ORG_DOMAIN = 'synthetic.omnea.co'
export const SYNTHETIC_USER_EMAIL = `sindy.synthetic@${SYNTHETIC_ORG_DOMAIN}`
export const SYNTHETIC_USER_FIRST_NAME = 'Sindy'
export const SYNTHETIC_USER_LAST_NAME = 'Synthetic'
export const SYNTHETIC_WORKFLOW_NAME = '[TEST WORKFLOW] Basic form validation'
// The intake-and-approval journey: an intake (file upload + conditionality) feeding an
// approval the synthetic user approves in-app. apps/verification-tests/e2e matches this
// name on the /start page as a literal, so any change must be applied in both places.
export const SYNTHETIC_INTAKE_APPROVAL_WORKFLOW_NAME = '[TEST WORKFLOW] Intake and approval'
// The mobile journey: a simple intake feeding an approval the mobile user actions in-app on a
// mobile viewport, so the mobile-only components (request cards, table search, form-viewer back
// button) render against the user's own request. apps/verification-tests/e2e matches this name
// on the /start page as a literal, so any change must be applied in both places.
export const SYNTHETIC_MOBILE_WORKFLOW_NAME = '[TEST WORKFLOW] Mobile intake and approval'

export type SyntheticJourneyUser = { id: string; email: string; firstName: string; lastName: string }

// Each browser journey authenticates as its own user, so journeys (and overlapping runs of
// the suite) never share a login or task inbox. /auth/signup provisions the auth user at test
// time; the seed creates the DB user that login and the approval assignee resolve to.
// apps/verification-tests/e2e mirrors these emails as literals, so keep them in sync.
export const SYNTHETIC_JOURNEY_USERS = {
  intakeApproval: {
    id: '1e6a9c74-3b2d-4f81-9a05-7c8e2d1b4f60',
    email: `intake-approval@${SYNTHETIC_ORG_DOMAIN}`,
    firstName: 'Intake',
    lastName: 'Approver',
  },
  mobile: {
    id: '2f7b8c05-4c3e-4a92-b016-8d9f3e2c5a71',
    email: `mobile@${SYNTHETIC_ORG_DOMAIN}`,
    firstName: 'Mobile',
    lastName: 'Journey',
  },
} satisfies Record<string, SyntheticJourneyUser>

// The email-login journey signs in through the real magic-link flow, so its user must be
// reachable in MailSlurp. MailSlurp's custom-domain quota is exhausted, so synthetic.omnea.co
// cannot be added; this one journey uses e2e-test.omnea.co (login email is transactionalEmailOnly,
// which bypasses the e2e-test send suppression). The address is per-environment, but the domain has
// a single catch-all inbox, so every stage's mail lands together and waiters must filter on the
// recipient (see apps/verification-tests/shared/mailSlurp.ts) or one stage consumes another stage's
// single-use magic link. The id is fixed and reused across stages
// (each stage is a separate database). apps/verification-tests/e2e mirrors the address builder as a
// literal, so keep them in sync.
export const SYNTHETIC_LOGIN_USER_ID = '3a8c1d92-6f4b-4e27-b1a0-9d5c8e3f2b41'
export const SYNTHETIC_LOGIN_USER_FIRST_NAME = 'Login'
export const SYNTHETIC_LOGIN_USER_LAST_NAME = 'Journey'
export const getSyntheticLoginEmail = (stage: string) => `login-${stage}@${TEST_EMAIL_DOMAIN}`

// The supplier-onboarding journey API-creates a request from this workflow (intake -> supplier
// onboarding step), then drives the supplier portal. It reuses createTestAccount's onboarding
// workflow + form builders, but under a synthetic-specific id (the createTestAccount workflow id
// already exists for testorg in the same database). apps/verification-tests/e2e mirrors this id +
// the intake question ids as literals, so keep them in sync.
export const SYNTHETIC_SUPPLIER_ONBOARDING_WORKFLOW_ID = '8b1e4c37-2d6a-4f95-a3c1-7e0d9f2b5a84'
export const SYNTHETIC_SUPPLIER_ONBOARDING_WORKFLOW_NAME = '[TEST WORKFLOW] Supplier onboarding'

// Intake -> CREATE_AGREEMENT block, for the agreement-created smoke test.
// apps/verification-tests/smoke mirrors this id as a literal; keep in sync.
export const SYNTHETIC_CREATE_AGREEMENT_WORKFLOW_ID = 'fcffb902-0869-46e2-b33d-fa1ff0f45589'
export const SYNTHETIC_CREATE_AGREEMENT_WORKFLOW_NAME = '[TEST WORKFLOW] Create agreement'

// Simple intake -> SUMMARY_APPROVAL task, for the email-summary-approval smoke test.
// apps/verification-tests/smoke mirrors this id as a literal; keep in sync.
export const SYNTHETIC_SUMMARY_APPROVAL_WORKFLOW_ID = '3bad50ef-706b-43cd-a872-98e9f5496ec4'
export const SYNTHETIC_SUMMARY_APPROVAL_WORKFLOW_NAME = '[TEST WORKFLOW] Summary approval'

// Receives the real summary-approval email, so it must be MailSlurp-reachable with a
// per-environment address and fixed id (same arrangement as the login journey user above).
// apps/verification-tests/smoke mirrors the address builder as a literal; keep in sync.
export const SYNTHETIC_SUMMARY_APPROVER_USER_ID = '60324f53-0028-4554-bb1b-3301e9555570'
export const SYNTHETIC_SUMMARY_APPROVER_FIRST_NAME = 'Summary'
export const SYNTHETIC_SUMMARY_APPROVER_LAST_NAME = 'Approver'
export const getSyntheticSummaryApproverEmail = (stage: string) => `summary-approval-${stage}@${TEST_EMAIL_DOMAIN}`

// Every user ensureSyntheticOrg seeds. The synthetic-org janitor
// (packages/backend/syntheticOrg) hard-deletes any user in the org NOT listed here, so a
// user added to the seed without being added here will be pruned overnight and break the suite.
export const SYNTHETIC_SEED_USER_IDS: readonly string[] = [
  SYNTHETIC_USER_ID,
  ...Object.values(SYNTHETIC_JOURNEY_USERS).map(({ id }) => id),
  SYNTHETIC_LOGIN_USER_ID,
  SYNTHETIC_SUMMARY_APPROVER_USER_ID,
]
